import {
  VStack,
  HStack,
  Divider,
  Text,
  Icon,
  Spinner,
  Badge,
  Stack,
} from "@chakra-ui/react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { KYDInternalCampaign } from "@common/interfaces/KYDCampaign";
import StatCard from "../../Components/StatCard";
import { FaEnvelope, FaPause, FaTicketAlt, FaHourglass } from "react-icons/fa";
import dayjs from "dayjs";
import { CalendarIcon } from "@chakra-ui/icons";
import { IoSend } from "react-icons/io5";
import { formatDate } from "src/utils";
import { KYDScope } from "@common/interfaces/ICognito";
import ShowIfHasScope from "src/Manager/ShowIfHasScope";

interface HeadlineProps {
  title: string;
  icon: React.ReactNode;
  children?: React.ReactNode;
}

const Headline = ({ title, icon, children }: HeadlineProps) => {
  return (
    <HStack gap={2} alignItems="center" justifyContent="flex-start" mb={2}>
      {icon}
      <Text
        fontSize="24px"
        fontWeight="600"
        textAlign="left"
        color="#1A202C"
        letterSpacing="-0.72px"
      >
        {title}
      </Text>
      {children}
    </HStack>
  );
};

interface SubheadlineProps {
  title: string;
}

const Subheadline = ({ title }: SubheadlineProps) => {
  return (
    <Text
      fontSize="16px"
      fontWeight="500"
      textAlign="left"
      color="rgba(26, 32, 44, 0.75)"
      letterSpacing="-0.48px"
      lineHeight="150%"
    >
      {title}
    </Text>
  );
};

const colorForStatus = (
  status: KYDInternalCampaign["status"],
  background: boolean = false
) => {
  if (status === "ACTIVE") return !background ? "yellow.400" : "yellow.100";
  if (status === "OPEN") return !background ? "blue.400" : "blue.100";
  if (status === "PAUSED") return !background ? "gray.400" : "gray.100";
  if (status === "COMPLETED") return !background ? "green.400" : "green.100";
  return !background ? "gray.400" : "gray.100";
};

const IconForStatus = ({
  status,
  color,
}: {
  status: KYDInternalCampaign["status"];
  color: string;
}) => {
  if (status === "ACTIVE") return <FaHourglass color={color} />;
  if (status === "OPEN") return <CalendarIcon color={color} />;
  if (status === "PAUSED") return <FaPause color={color} />;
  if (status === "COMPLETED") return <IoSend color={color} />;
  return <CalendarIcon color={color} />;
};

const TextForStatus = ({
  status,
}: {
  status: KYDInternalCampaign["status"];
}) => {
  const TextComp = ({ children }: { children: React.ReactNode }) => (
    <Text fontSize="12px" textTransform="uppercase">
      {children}
    </Text>
  );

  if (status === "ACTIVE") return <TextComp>Active</TextComp>;
  if (status === "OPEN") return <TextComp>Scheduled</TextComp>;
  if (status === "PAUSED") return <TextComp>Paused</TextComp>;
  if (status === "COMPLETED") return <TextComp>Sent</TextComp>;

  return <TextComp>Draft</TextComp>;
};

const CampaignStatusBadge = ({
  status,
}: {
  status: KYDInternalCampaign["status"];
}) => {
  return (
    <Badge
      background={colorForStatus(status, true)}
      color={colorForStatus(status)}
    >
      <HStack gap={2} alignItems="center" justifyContent="center">
        <IconForStatus status={status} color={colorForStatus(status)} />
        <TextForStatus status={status} />
      </HStack>
    </Badge>
  );
};

interface MetricStatCardProps {
  title: string;
  value: string | number;
  highlight?: boolean;
  isKYD?: boolean;
  info?: string;
  percentage?: number;
  [key: string]: unknown;
}

const MetricStatCard = (props: MetricStatCardProps) => {
  return <StatCard {...props} minH={{ base: "68px", md: "80px" }} />;
};

const Metrics = ({
  isMobile,
  campaign,
  onCancelCampaign,
  permissions,
}: {
  isMobile: boolean;
  permissions: KYDScope[];
  campaign: KYDInternalCampaign;
  onCancelCampaign: () => void;
}) => {
  const sentInFuture = dayjs(campaign.send_at || new Date()).isAfter(dayjs());
  if (!campaign) return null;
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <VStack
        bg="white"
        h="100%"
        alignItems="flex-start"
        position="relative"
        gap={4}
      >
        <HStack
          gap={2}
          py={{ base: 2, md: 6 }}
          alignItems="center"
          w="full"
          px={{ base: 4, md: 6 }}
          borderTop="1px solid rgba(0,0,0,0.2)"
          borderBottom="1px solid rgba(0,0,0,0.12)"
        >
          <Text
            color="#888888"
            fontWeight="500"
            fontSize="14px"
            letterSpacing="-0.42px"
            lineHeight="140%"
          >
            <CampaignStatusBadge status={campaign.status} />
          </Text>
          <HStack gap={1}>
            <Text textAlign="left" color="#" lineHeight="24px" fontSize="small">
              {sentInFuture ? "For" : "On"}
            </Text>
            <Text as="span" fontWeight="bold" fontSize="small">
              {formatDate(dayjs(campaign.send_at || new Date()))}
            </Text>
          </HStack>
          <ShowIfHasScope
            permissions={permissions}
            checkFor={["crm/campaigns:write", "crm/write"]}
          >
            {sentInFuture && (
              <HStack
                ml={"auto"}
                px={2}
                color="#1D74FF"
                as="button"
                spacing={1}
                onClick={() => onCancelCampaign()}
                cursor="pointer"
              >
                <Text fontSize="small" color="#1D74FF">
                  Edit
                </Text>
              </HStack>
            )}
          </ShowIfHasScope>
        </HStack>

        <VStack gap={2} alignItems="flex-start" px={{ base: 4, md: 6 }}>
          <Text
            color="#888888"
            fontWeight="500"
            fontSize="14px"
            letterSpacing="-0.42px"
            lineHeight="140%"
          >
            {campaign.segments.length > 1
              ? "Segments Delivered To"
              : "Segment Delivered To"}
          </Text>
          <Text
            fontSize="20px"
            fontWeight="500"
            textAlign="left"
            color="#222"
            lineHeight="24px"
            width={"100%"}
          >
            {campaign.segments.map((segment) => segment.name).join(", ")}
          </Text>
        </VStack>
        <Divider width={"full"} borderColor="#00000033" />
        <VStack
          w="100%"
          alignItems="flex-start"
          spacing={2}
          px={{ base: 4, md: 6 }}
        >
          <Headline
            title="KYD Ticket Sales"
            icon={<Icon as={FaTicketAlt} color="#A7A7A7" />}
          >
            {campaign.status === "ACTIVE" ? <Spinner size="sm" /> : null}
          </Headline>
          <VStack spacing={4} w="100%" alignItems="flex-start" p={0}>
            <Stack
              w="100%"
              spacing={4}
              direction={{ base: "column", sm: "row" }}
            >
              <MetricStatCard
                title="Tickets"
                value={`${campaign.display.tickets}`}
                w={{ base: "100%", sm: "auto" }}
                flex={{ base: "0 0 auto", sm: "1" }}
              />
              <MetricStatCard
                title="Avg. Order Value"
                value={`${campaign.display.average_order_value}`}
                w={{ base: "100%", sm: "auto" }}
                flex={{ base: "0 0 auto", sm: "1" }}
              />
            </Stack>
            <Stack
              w="100%"
              spacing={4}
              direction={{ base: "column", sm: "column", md: "row" }}
            >
              <MetricStatCard
                highlight
                isKYD
                title="Revenue"
                value={`${campaign.display.revenue}`}
                flex={{ base: "0 0 auto", md: "1" }}
                w={{ base: "100%", md: "auto" }}
              />
              <MetricStatCard
                title="ROAS"
                highlight
                isKYD
                value={campaign.display.roas || "-"}
                flex={{ base: "0 0 auto", md: "1" }}
                w={{ base: "100%", md: "auto" }}
              />
            </Stack>
          </VStack>
        </VStack>
        <Divider width={"full"} borderColor="#00000033" />

        <VStack
          w="100%"
          alignItems="flex-start"
          gap={3}
          px={{ base: 4, md: 6 }}
        >
          <Headline
            title="Email Metrics"
            icon={<Icon as={FaEnvelope} color="#A7A7A7" />}
          />
          <VStack w="full" gap={2} alignItems="flex-start">
            <Subheadline title="Delivery" />
            <HStack
              w="100%"
              spacing={4}
              mb={1}
              direction={{ base: "column", sm: "row" }}
              wrap="wrap"
            >
              <MetricStatCard
                title="Delivered"
                value={`${campaign.display.deliveries}`}
                flex={{ base: "1 1 100%", sm: "1" }}
                minW={{ base: "100%", sm: "0" }}
              />
              <MetricStatCard
                title="Opens"
                value={`${campaign.display.unique_opens}`}
                flex={{ base: "1 1 100%", sm: "1" }}
                minW={{ base: "100%", sm: "0" }}
              />
              <MetricStatCard
                title="Open Rate"
                value={`${campaign.display.open_rate}`}
                flex={{ base: "1 1 100%", sm: "1" }}
                minW={{ base: "100%", sm: "0" }}
              />
            </HStack>
          </VStack>
          <VStack w="full" gap={2} alignItems="flex-start">
            <Subheadline title="Engagement" />
            <VStack spacing={4} w="100%" alignItems="flex-start" p={0}>
              <HStack
                w="100%"
                spacing={4}
                direction={{ base: "column", sm: "row" }}
              >
                <MetricStatCard
                  title="Clicks"
                  value={`${campaign.display.unique_clicks}`}
                  flex={{ base: "1 1 100%", sm: "1" }}
                  minW={{ base: "100%", sm: "0" }}
                />
                <MetricStatCard
                  title="Click Through Rate"
                  value={`${campaign.display.click_through_rate}`}
                  flex={{ base: "1 1 100%", sm: "2" }}
                  minW={{ base: "100%", sm: "0" }}
                />
              </HStack>
              <HStack
                w="100%"
                spacing={4}
                direction={{ base: "column", sm: "row" }}
              >
                <MetricStatCard
                  title="Unsubscribes"
                  value={`${campaign.display.unsubscribes}`}
                  flex={{ base: "1 1 100%", sm: "1" }}
                  minW={{ base: "100%", sm: "0" }}
                />
                {/* <MetricStatCard
                title="Unsubscribed Rate"
                value={`${campaign.analytics.open_rate}%`}
                flex="1"
              /> */}
              </HStack>
            </VStack>
          </VStack>
          <VStack w="full" gap={2} alignItems="flex-start">
            <Subheadline title="Negative" />
            <HStack
              w="100%"
              spacing={2}
              mb={8}
              direction={{ base: "column", sm: "row" }}
            >
              <MetricStatCard
                title="Bounces"
                value={`${campaign.analytics.bounces}`}
                flex={{ base: "1 1 100%", sm: "1" }}
                minW={{ base: "100%", sm: "0" }}
              />
              <MetricStatCard
                title="Complaints"
                value={`${campaign.analytics.complaints}`}
                flex={{ base: "1 1 100%", sm: "1" }}
                minW={{ base: "100%", sm: "0" }}
              />
            </HStack>
          </VStack>
        </VStack>
      </VStack>
    </LocalizationProvider>
  );
};

export default Metrics;
